import { SendCardLinkInput } from '@lub0v/santa-utils';
import type { TFunction } from 'i18next';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import { Trans } from 'react-i18next';
import { Checkbox, Hint, EmailInput, Link, Stack, Typography } from '@src/components';
import { PRIVACY_POLICY_PATH } from '@src/routing/routes';
import { translateFieldError, validateFieldError } from '@src/validation';

export function emailInviteSendError(
  error: { message?: string; data?: unknown } | undefined,
  t: TFunction,
): string {
  if (error?.message === 'EMAIL_ALREADY_IN_GAME') {
    const data = error.data as { cardName?: unknown; cardId?: unknown } | undefined;
    const name =
      typeof data?.cardName === 'string'
        ? data.cardName
        : typeof data?.cardId === 'number'
          ? `#${data.cardId}`
          : '';
    return t(
      name ? 'box.organizer.email.alreadyInGame' : 'box.organizer.email.alreadyInGameUnknown',
      { name },
    );
  }
  return t(resolveApiErrorKey(error?.message || 'box.organizer.linkFailed'));
}

export interface EmailInviteFormProps {
  /** Why the organizer is being asked for someone else's address. */
  intro: string;
  email: string;
  onEmailChange: (email: string) => void;
  /** Check the address when the field is left, the way the login field does. */
  onEmailBlur?: () => void;
  emailLabel: string;
  /** What the address is used for, and that nobody else will see it. */
  hint: string;
  consentGiven: boolean;
  onConsentChange: (given: boolean) => void;
  /** Field-level: invalid address. Lives on the input. */
  emailError?: string | null;
  /** Request-level: the send itself failed. */
  error?: string | null;
  testId?: string;
}

/**
 * The `@validate` rule on `SendCardLinkInput.email` — the same check the API
 * will run, so an address that leaves this field is one the server will take.
 */
export function emailInviteError(email: string, t: TFunction): string | undefined {
  const error = validateFieldError(new SendCardLinkInput(), 'email', { email: email.trim() });
  return error ? translateFieldError(error, t) : undefined;
}

/**
 * Body of the send-by-email dialog. The consent line is not decoration: the
 * organizer is handing us a third party's address, so the dialog will not send
 * until they say they may.
 */
export function EmailInviteForm({
  intro,
  email,
  onEmailChange,
  onEmailBlur,
  emailLabel,
  hint,
  consentGiven,
  onConsentChange,
  emailError,
  error,
  testId,
}: EmailInviteFormProps) {
  return (
    <Stack gap={4} wide>
      <Typography as="p" role="body/base">
        {intro}
      </Typography>
      <EmailInput
        value={email}
        onValueChange={onEmailChange}
        onBlur={onEmailBlur}
        label={emailLabel}
        hint={hint}
        error={emailError ?? undefined}
        autoComplete="off"
        testId={testId ? `${testId}-email` : undefined}
      />
      <Checkbox
        checked={consentGiven}
        onCheckedChange={onConsentChange}
        label={
          <Trans
            i18nKey="box.organizer.email.consent"
            components={{
              policy: <Link href={PRIVACY_POLICY_PATH} variant="inline" />,
            }}
          />
        }
        testId={testId ? `${testId}-consent` : undefined}
      />
      {error ? <Hint tone="error" testId={testId ? `${testId}-error` : undefined}>{error}</Hint> : null}
    </Stack>
  );
}
